/**
 * Cost calculation utilities for ship components and operations
 */

import { ShipBuilderState } from "../../types/ship";
import {
  calculateHullCost,
  calculateArmorCost,
  calculateDriveCost,
} from "./hullCalculations";
import { calculatePowerPlantStats } from "./powerCalculations";
import { calculateComputerCost } from "./computerCalculations";
import { calculateSoftwareCost } from "./softwareCalculations";
import { calculateOptionalSystemsCost } from "./optionalSystems";
import { getDriveCost } from "../../constants/drives";
// Removed unused imports - now using direct footer calculations
import { calculateBridgeCost as calculateTravellerBridgeCost } from "../../constants/bridges";
import { CalculationResult } from "../../types/calculations";
import { CrewComposition } from "../../types/ship";
import { StateroomConfig, StateroomStats } from "../../types/stateroom";
import { BridgeType } from "../../types/bridge";
import { STATEROOM_CONFIG } from "../../constants/ship";
import {
  calculateHullAndArmorStats,
  HullAndArmorStatsInput,
} from "./hullAndArmorCalculations";
import { calculateBridgeStats } from "./bridgeCalculations";
import { calculateSensorStats } from "./sensorCalculations";
import { calculateComputerStats } from "./computerCalculations";
import { ArmorConfiguration } from "../../types/ship";
import { HullConfiguration } from "../../types/hull";
import { ArmorType } from "../../types/armor";

/**
 * Calculates total cost including all components
 * @param state - Ship builder state
 * @param weaponCounts - Weapon counts
 * @returns Calculation result with total cost or error
 */
/**
 * Calculate total cost using the same method as CostDebugger (single source of truth)
 * This ensures consistency between the debug display and system calculations
 */
export const calculateTotalCostFromDebugger = (
  state: ShipBuilderState,
  optionalSystems: any,
  hullAndArmorStats: any,
  shipStats: any,
): CalculationResult<number> => {
  try {
    console.log("🚀 calculateTotalCostFromDebugger - STARTING CALCULATION:");
    console.log("  - state:", state);
    console.log("  - state.sensors:", state.sensors);
    let totalCost = 0;
    
    // Hull & Armor - use totalCost from hullAndArmorStats (same as CostDebugger)
    const hullAndArmorCost = hullAndArmorStats?.totalCost || 0;
    console.log("💰 Hull & Armor cost:", hullAndArmorCost);
    totalCost += hullAndArmorCost;

    // Drives - use getDriveCost function (same as CostDebugger)
    let driveCost = 0;
    if (state.drives && state.drives.length > 0) {
      const driveCosts = state.drives.map(drive => {
        return getDriveCost(drive.type, drive.rating, state.shipSize || 0);
      });
      driveCost = driveCosts.reduce((sum, cost) => sum + cost, 0);
      console.log("💰 Drive cost:", driveCost);
      totalCost += driveCost;
    }

    // Power Plant
    if (state.powerPlant) {
      console.log("💰 Power Plant cost:", state.powerPlant.cost || 0);
      totalCost += state.powerPlant.cost || 0;
    }

    // Bridge
    if (state.bridge) {
      console.log("💰 Bridge cost:", state.bridge.cost || 0);
      totalCost += state.bridge.cost || 0;
    }

    // Sensors
    if (state.sensors) {
      console.log("💰 calculateTotalCostFromDebugger - Sensors DEBUG:");
      console.log("  - state.sensors:", state.sensors);
      console.log("  - state.sensors.cost:", state.sensors.cost);
      console.log("  - totalCost BEFORE sensors:", totalCost);
      console.log("  - Adding to totalCost:", state.sensors.cost || 0);
      totalCost += state.sensors.cost || 0;
      console.log("  - totalCost after sensors:", totalCost);
    }

    // Computer - use proper calculation that includes options
    if (state.computer) {
      const primaryCost = state.computer.primary ? calculateComputerCost(state.computer.primary) : 0;
      const backupCost = state.computer.backup ? calculateComputerCost(state.computer.backup) : 0;
      const computerCost = primaryCost + backupCost;
      console.log("💰 calculateTotalCostFromDebugger - Computer DEBUG:");
      console.log("  - state.computer:", state.computer);
      console.log("  - primary computer:", state.computer.primary);
      console.log("  - backup computer:", state.computer.backup);
      console.log("  - primary cost (with options):", primaryCost);
      console.log("  - backup cost (with options):", backupCost);
      console.log("  - total computer cost:", computerCost);
      totalCost += computerCost;
    }

    // Software
    if (state.software && state.computer && (state.computer.primary || state.computer.backup)) {
      totalCost += calculateSoftwareCost(state.software, state.computer) * 1_000_000;
    }

    // Weapons & Defensive Systems - use EXACT same method as WeaponsFooterSummary
    let totalWeaponCost = 0;
    let totalAmmoCost = 0;
    let totalDefensiveCost = 0;
    
    // Weapon costs (from mount.cost - includes mount + weapon)
    if (state.weaponMounts) {
      state.weaponMounts.forEach((mount) => {
        totalWeaponCost += mount.cost || 0;
      });
    }
    
    // Ammo costs (from weapon.ammo.costPerReload * weapon.extraReloads)
    if (state.weaponMounts) {
      state.weaponMounts.forEach((mount) => {
        mount.weapons.forEach((weapon) => {
          if (weapon.ammo && weapon.extraReloads) {
            totalAmmoCost += weapon.ammo.costPerReload * weapon.extraReloads;
          }
        });
      });
    }
    
    // Defensive systems costs (from sys.cost * quantity)
    if (state.defensiveSystems) {
      state.defensiveSystems.forEach((sys) => {
        const quantity = sys.quantity || 1;
        totalDefensiveCost += (sys.cost || 0) * quantity;
      });
    }
    
    // Add weapons & defensive total (same as footer calculation)
    totalCost += totalWeaponCost + totalAmmoCost + totalDefensiveCost;

    // Staterooms - use same method as CostDebugger
    if (state.stateroomConfig) {
      let stateroomCost = 0;
      stateroomCost += (state.stateroomConfig.single || 0) * 500000; // MCr0.5 each
      stateroomCost += (state.stateroomConfig.double || 0) * 500000; // MCr0.5 each (same as single)
      if (state.stateroomConfig.lowBerths) {
        stateroomCost += (state.stateroomConfig.lowBerths.count || 0) * 50000; // MCr0.05 each
      }
      if (state.stateroomConfig.emergencyLowBerths) {
        stateroomCost += (state.stateroomConfig.emergencyLowBerths.count || 0) * 1000000; // MCr1 each
      }
      // Common area cost (if any)
      stateroomCost += (state.stateroomConfig.commonArea || 0) * 50000; // MCr0.05 per ton
      totalCost += stateroomCost;
    }

    // Optional Systems - use same method as CostDebugger
    const optionalSystemsCost = [
      ...optionalSystems.structure,
      ...optionalSystems.modularHull,
      ...optionalSystems.power,
      ...optionalSystems.drives,
      ...optionalSystems.fuel,
      ...optionalSystems.accommodations,
      ...optionalSystems.bridge,
      ...optionalSystems.cargo,
      ...optionalSystems.drones,
      ...optionalSystems.sensors,
      ...optionalSystems.external,
      ...optionalSystems.internal,
      ...optionalSystems.software,
      ...optionalSystems.commonAreas,
    ].reduce((sum, system) => sum + (system.cost || 0), 0);
    totalCost += optionalSystemsCost;

    // Fuel Tanks - no cost according to Traveller rules (same as CostDebugger)
    const fuelTankCost = 0; // No cost for fuel tank space
    totalCost += fuelTankCost;

    // Supply Units - MCr0.1 per ton (same as CostDebugger)
    if (state.supplyTons && state.supplyTons > 0) {
      totalCost += state.supplyTons * 100000;
    }

    // Ammunition Storage (separate from weapon ammo - this is for stored ammo)
    if (state.ammunitionStorage) {
      const ammunitionCost = state.ammunitionStorage.reduce((total, ammo) => {
        const quantity = ammo.quantity || 0;
        return total + (ammo.cost * quantity);
      }, 0);
      totalCost += ammunitionCost;
    }

    console.log("🏁 calculateTotalCostFromDebugger - FINAL RESULT:");
    console.log("  - Final totalCost:", totalCost);
    
    return {
      success: true,
      data: totalCost,
    };
  } catch (error) {
    console.error("❌ calculateTotalCostFromDebugger - ERROR:", error);
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate total cost: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};

export const calculateTotalCost = (
  state: ShipBuilderState,
  weaponCounts: Record<string, number>,
): CalculationResult<number> => {
  try {
    let totalCost = 0;

    // Hull and Armor - only calculate if hull has been configured by user
    if (state.hull && state.hull.cost > 0) {
      const armorConfig: ArmorConfiguration | null = state.hull.armor
        ? {
            type: state.hull.armor.type,
            rating: state.hull.armor.rating,
            // The following are not in the Armor type, but may be needed by the calculation.
            // I'll add them with default values, but this might need revisiting.
            costPerTon: state.hull.armor.costPerTon || 0,
            weightPerTon: state.hull.armor.weightPerTon || 0,
            description: state.hull.armor.description || "",
          }
        : null;

      const hullInput: HullAndArmorStatsInput = {
        shipSize: state.shipSize || 0,
        hull: state.hull,
        armor: armorConfig,
        hullConfiguration: state.hull.configuration ?? null,
        hullOptions: state.hull.options || [],
        specializedHull: state.hull.specializedType ?? null,
        additionalHull: state.hull.additionalType ?? null,
        techLevel: state.techLevel || 7,
      };
      const hullStats = calculateHullAndArmorStats(hullInput);
      if (hullStats.success && hullStats.data) {
        totalCost += hullStats.data.totalCost;
      }
    }

    // Drives
    if (state.drives && state.drives.length > 0) {
      const driveCost = state.drives.reduce((total, drive) => {
        return (
          total +
          calculateDriveCost(drive.type, drive.rating, state.shipSize || 0)
        );
      }, 0);
      totalCost += driveCost;
    }

    // Power Plant
    if (state.powerPlant) {
      totalCost += state.powerPlant.cost || 0;
    }

    // Bridge - only calculate if bridge has been configured by user
    if (state.bridge && state.bridge.cost > 0) {
      const bridgeStats = calculateBridgeStats(
        state.bridge,
        state.shipSize || 0,
      );
      if (bridgeStats.success && bridgeStats.data) {
        totalCost += bridgeStats.data.cost;
      }
    }

    // Sensors
    if (state.sensors) {
      const sensorStats = calculateSensorStats(
        state.sensors,
        state.shipSize || 0,
      );
      if (sensorStats.success && sensorStats.data) {
        totalCost += sensorStats.data.cost;
      }
    }

    // Computer
    if (state.computer) {
      const computerStats = calculateComputerStats({
        computer: state.computer,
      });
      if (computerStats.success && computerStats.data) {
        totalCost += computerStats.data.totalCost;
      }
    }

    // Weapons, Ammo, and Defensive Systems (from weaponMounts and defensiveSystems)
    if (state.weaponMounts) {
      const { cost: weaponMountsCost } =
        require("./weaponCalculations").calculateWeaponMountsTotals(
          state.weaponMounts,
        );
      totalCost += weaponMountsCost;
    }
    if (state.defensiveSystems) {
      const { cost: defensiveSystemsCost } =
        require("./weaponCalculations").calculateDefensiveSystemsTotals(
          state.defensiveSystems,
        );
      totalCost += defensiveSystemsCost;
    }

    // Ammunition Storage
    if (state.ammunitionStorage) {
      const { cost: ammunitionCost } =
        require("./weaponCalculations").calculateAmmunitionStorageTotals(
          state.ammunitionStorage,
        );
      totalCost += ammunitionCost;
    }

    // Optional Systems (excluding external cargo mounts - cost calculated separately)
    if (state.optionalSystems) {
      totalCost += state.optionalSystems.reduce((sum, system) => {
        // External cargo mount cost is calculated based on capacity, not fixed cost
        if (
          system.type === "cargo" &&
          system.subtype === "externalCargoMount"
        ) {
          return sum;
        }
        return sum + (system.cost || 0);
      }, 0);
    }

    // Staterooms
    if (state.stateroomConfig) {
      const stateroomStats = calculateStateroomStats(state.stateroomConfig);
      if (stateroomStats.success && stateroomStats.data) {
        totalCost += stateroomStats.data.cost;
      }
    }

    // External Cargo Mounts (cost calculated based on capacity)
    if (state.optionalSystems) {
      const externalCargoMounts = state.optionalSystems.filter(
        (system) =>
          system.type === "cargo" && system.subtype === "externalCargoMount",
      );
      externalCargoMounts.forEach((mount) => {
        // Type guard to ensure we're working with a CargoSystem
        if (mount.type === "cargo" && mount.subtype === "externalCargoMount") {
          const capacity =
            (mount as any).properties?.externalCargoCapacity || 0;
          totalCost += capacity * 1000; // Cr1000 per ton
        }
      });
    }

    // Software - only calculate if computer has been configured by user
    if (state.software && state.computer && (state.computer.primary || state.computer.backup)) {
      totalCost +=
        calculateSoftwareCost(state.software, state.computer) * 1_000_000;
    }

    // Supply Units - MCr0.1 per ton
    if (state.supplyTons && state.supplyTons > 0) {
      totalCost += state.supplyTons * 100000; // MCr0.1 per ton = 100,000 Cr per ton
    }

    // Cargo, Fuel - Assuming these have a simple cost for now.
    if (state.cargo?.cost) totalCost += state.cargo.cost;
    if (state.fuel?.cost) totalCost += state.fuel.cost;

    return {
      success: true,
      data: totalCost,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate total cost: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};

/**
 * Calculates stateroom statistics including costs
 * @param config - Stateroom configuration
 * @returns Calculation result with stateroom stats or error
 */
export const calculateStateroomStats = (
  config: StateroomConfig,
): CalculationResult<StateroomStats> => {
  try {
    let totalCost = 0;
    let totalTons = 0;

    // Calculate single and double staterooms only
    if (config.single > 0) {
      const singleSpec = STATEROOM_CONFIG.single;
      totalCost += config.single * singleSpec.cost;
      totalTons += config.single * singleSpec.tons;
    }
    if (config.double > 0) {
      const doubleSpec = STATEROOM_CONFIG.double;
      totalCost += config.double * doubleSpec.cost;
      totalTons += config.double * doubleSpec.tons;
    }
    // Removed quad staterooms
    // if (config.quad > 0) { ... }

    // Calculate low berths
    if (config.lowBerths && config.lowBerths.count > 0) {
      totalCost += config.lowBerths.count * (config.lowBerths.cost || 0);
      totalTons += config.lowBerths.count * (config.lowBerths.tons || 0);
    }

    // Calculate emergency low berths
    if (config.emergencyLowBerths && config.emergencyLowBerths.count > 0) {
      totalCost +=
        config.emergencyLowBerths.count * (config.emergencyLowBerths.cost || 0);
      totalTons +=
        config.emergencyLowBerths.count * (config.emergencyLowBerths.tons || 0);
    }

    return {
      success: true,
      data: {
        cost: totalCost,
        tons: totalTons,
        totalRooms: config.single + config.double,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate stateroom stats: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};

/**
 * Calculate the total cost of all ship components
 * @param state - Ship builder state
 * @returns Total ship cost
 */
export const calculateTotalShipCost = (state: ShipBuilderState): number => {
  // Only calculate hull cost if hull has been configured by user
  const hullCost = state.hull && state.hull.cost > 0 
    ? calculateHullCost(
        state.shipSize || 0,
        (state.hull?.configuration as HullConfiguration) || {
          type: "standard",
          name: "Standard",
          description: "Standard hull",
          costPerTon: 1,
          costModifier: 1,
        },
      )
    : 0;
  
  const armorCost = calculateArmorCost(
    (state.armor?.type as ArmorType) || "titaniumSteel",
    state.shipSize || 0,
  );
  const driveCost =
    state.drives?.reduce((total, drive) => {
      return (
        total +
        calculateDriveCost(drive.type, drive.rating, state.shipSize || 0)
      );
    }, 0) || 0;
  const powerPlantStats = state.powerPlant
    ? calculatePowerPlantStats(
        state.powerPlant,
        state.shipSize || 0,
        state.drives || [],
      )
    : {
        success: false,
        data: {
          cost: 0,
          tons: 0,
          power: 0,
          maintenanceCost: 0,
          powerRequired: 0,
        },
      };
  const powerPlantCost = powerPlantStats.success
    ? powerPlantStats.data?.cost || 0
    : 0;
  const bridgeCost = state.bridge && state.bridge.cost > 0
    ? calculateBridgeCost(state.bridge.type, state.shipSize || 0)
    : 0;
  const computer = state.computer?.primary || state.computer?.backup;
  const computerCost = computer ? calculateComputerCost(computer) : 0;
  const softwareCost = (state.computer && (state.computer.primary || state.computer.backup)) 
    ? calculateSoftwareCost(state.software || { installed: [], running: [] }, state.computer) 
    : 0;
  const optionalSystemsCost = calculateOptionalSystemsCost(
    state.optionalSystems,
  );
  // const crewCost = calculateCrewCosts(state.crewComposition);
  const stateroomCost =
    calculateStateroomStats(state.stateroomConfig)?.data?.cost || 0;
  const fuelCost = state.fuel ? state.fuel.cost : 0;
  const cargoCost = state.cargo ? state.cargo.cost : 0;
  const weaponsCost = state.weapons.reduce(
    (total, weapon) => total + weapon.cost,
    0,
  );
  const weaponMountsCost =
    state.weaponMounts?.reduce((total, mount) => total + mount.cost, 0) || 0;
  const defensiveSystemsCost =
    state.defensiveSystems?.reduce((total, system) => {
      const quantity = system.quantity || 1;
      return total + ((system.cost || 0) * quantity);
    }, 0) ||
    0;

  return (
    hullCost +
    armorCost +
    driveCost +
    powerPlantCost +
    bridgeCost +
    computerCost +
    softwareCost +
    optionalSystemsCost +
    stateroomCost +
    fuelCost +
    cargoCost +
    weaponsCost +
    weaponMountsCost +
    defensiveSystemsCost
  );
};

/**
 * Calculate bridge cost based on ship size and bridge type
 * @param shipSize - Ship size in tons
 * @param bridgeType - Bridge type
 * @returns Bridge cost
 */
export const calculateBridgeCost = (
  bridgeType: BridgeType,
  shipSize: number,
): number => {
  return calculateTravellerBridgeCost(bridgeType, shipSize);
};

/**
 * Calculate monthly operational costs for a ship
 * @param state - Ship builder state
 * @param crewComposition - Crew composition
 * @returns Monthly operational costs
 */
export const calculateMonthlyCosts = (
  state: ShipBuilderState,
  crewComposition: CrewComposition,
): CalculationResult<number> => {
  try {
    let totalCost = 0;

    // Crew costs
    // totalCost += calculateCrewCosts(crewComposition);

    // Power plant maintenance (if available)
    if (state.powerPlant) {
      totalCost += state.powerPlant.maintenanceCost || 0;
    }

    // Bridge maintenance (if available)
    if (state.bridge) {
      totalCost += state.bridge.maintenanceCost || 0;
    }

    // Armor maintenance (if available)
    if (state.armor) {
      totalCost += state.armor.maintenanceCost || 0;
    }

    return {
      success: true,
      data: totalCost,
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate monthly costs: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};
